import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { useEffect } from 'react';

import { PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants/pagination';

import {
  createInfiniteQuery,
  InfiniteQueryOptionsType,
} from '~/utils/createInfiniteQuery';

import { queryKeysSite } from '../queryKeys';

import { fetchSites } from './fetchSites';
import { type SitesQueryParams, type SitesResponse } from './types';
import { zodSitesQueryParamsSchema } from './validations';

/**
 * Generates the query options for the sites query.
 *
 * @param queryParams - The query parameters for fetching sites.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getSitesQueryOptions = ({
  queryParams,
  options,
}: {
  queryParams: Partial<SitesQueryParams>;
  options?: Omit<UseQueryOptions<SitesResponse>, 'queryKey' | 'queryFn'>;
}) => {
  return {
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }: { signal: AbortSignal }) =>
      fetchSites(queryParams, { signal }),
    queryKey: queryKeysSite.getAll(queryParams),
    staleTime: ms('60s'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting the data for all sites with given query parameters.
 * Handles pagination and prefetches the next page of sites for better performance.
 *
 * @param queryParams - The query parameters for fetching sites.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/get_sites_site_get
 */
export const useQuerySites = (
  queryParams: Partial<SitesQueryParams>,
  options?: Omit<UseQueryOptions<SitesResponse>, 'queryKey' | 'queryFn'>,
) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (queryParams?.searchString && queryParams.searchString.length > 0) {
      // Only prefetch if the user is not searching, to prevent firing lots of requests for costly queries.
      return;
    }

    void queryClient.prefetchQuery(
      getSitesQueryOptions({
        options,
        queryParams: {
          ...queryParams,
          offset:
            (queryParams.offset ?? 0) +
            (queryParams.limit ?? PAGINATION_PAGE_SIZE_DEFAULT),
        },
      }),
    );
  }, [queryParams, options, queryClient]);

  return useQuery<SitesResponse>({
    ...getSitesQueryOptions({ options, queryParams }),
    placeholderData: keepPreviousData,
  });
};

/**
 * React Query based custom hook for getting the data for all sites with infinite scrolling.
 * Uses useInfiniteQuery to handle loading more data as the user scrolls.
 *
 * @param queryParams - The query parameters for fetching sites.
 * @param options - Additional options for the useInfiniteQuery hook.
 * @returns The result of the useInfiniteQuery hook.
 */
export const useQuerySitesInfinite = (
  queryParams: Partial<SitesQueryParams>,
  options?: InfiniteQueryOptionsType<SitesResponse, Error>,
) => {
  const validatedQueryParams = zodSitesQueryParamsSchema.parse(queryParams);

  const infiniteQueryOptions = {
    queryFn: async ({
      pageParam: pageParameter = 0,
      signal,
    }: {
      pageParam?: number;
      signal: AbortSignal;
    }) =>
      fetchSites(
        {
          ...validatedQueryParams,
          offset: pageParameter,
        },
        { signal },
      ),
    queryKey: queryKeysSite.getAllInfinite(validatedQueryParams),
    ...options,
  };

  return createInfiniteQuery<SitesResponse>(
    validatedQueryParams,
    infiniteQueryOptions,
  );
};
